import React from "react";
import { Button, Icon } from "@showitinc/showit-wc";
import UIActions from "../../actions/UIActions";
import AssetUtils from "../../utils/AssetUtils";
import { getAssetFileNameFromKey } from "../../utils/AssetNameUtils";
import useUserAssets from "../../hooks/query/useUserAssets";
import AppConstants from "../../constants/AppConstants";
import styles from "./LottieSelectInput.module.css";

const { ASSET_LIBRARY_MODE_CHOOSER, ASSET_TYPE_LOTTIE } = AppConstants;

/**
 * Compact Lottie file selector with LottieFiles icon.
 * Shows centered icon with text below, similar to video select but smaller.
 *
 * @param {Object} props
 * @param {boolean} props.disabled - Whether the input is disabled
 * @param {boolean} props.enableDelete - Show delete button when file selected
 * @param {Function} props.onChange - Callback when asset changes
 * @param {string} props.propName - Property name for the change event
 * @param {Map} props.value - Current lottie asset value (Immutable Map)
 */
const LottieSelectInput = ({ disabled, enableDelete = false, onChange, propName, value }) => {
  const { assets } = useUserAssets();

  const _updateSelection = (asset) => {
    if (onChange) {
      const propInfo = {
        name: propName,
        value: asset,
        origValue: value,
      };
      onChange(propInfo);
    }
  };

  const _setAsset = (asset) => {
    _updateSelection(AssetUtils.getElementAsset(asset));
  };

  const _deleteAsset = (e) => {
    e.stopPropagation();
    _updateSelection(null);
  };

  const _selectAsset = () => {
    if (disabled) return;

    UIActions.toggleAssetChooser({
      btnLabel: "Select",
      onSelect: _setAsset,
      mode: ASSET_LIBRARY_MODE_CHOOSER,
      title: "Select Lottie",
      filter: ASSET_TYPE_LOTTIE,
    });
  };

  const key = value ? value.get("key") : "";
  const fileName = key ? getAssetFileNameFromKey(assets, key) : null;

  return (
    <div className={styles.wrapper}>
      <div
        data-testid="lottie-file-select"
        className={`${styles.container} ${disabled ? styles.disabled : ""}`}
        onClick={_selectAsset}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => e.key === "Enter" && _selectAsset()}
      >
        <Icon type="lottieFiles" size="20" className={styles.lottieIcon} />
        <span className={styles.label}>{fileName || "Choose Lottie File"}</span>
      </div>
      {enableDelete && key && !disabled && (
        <Button
          className={styles.deleteButton}
          variant="icon"
          iconPrefix="trash"
          size="16"
          onSIClick={_deleteAsset}
        />
      )}
    </div>
  );
};

export default LottieSelectInput;
